import { useCallback } from 'react';

import { useInitGooglePlacesService } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/hooks/useInitGooglePlacesService';
import { PlaceType } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/type';
import { FilterLocationOption } from '~types';
import {
  mapGooglePeriodsToOperatingHours,
  parseGooglePlaceInfoForCreateAddress,
} from '~utils/google.util';

export function useGooglePlacesService() {
  const { placesService } = useInitGooglePlacesService();

  const handleGetGooglePlaceDetail = useCallback(
    (value: google.maps.places.AutocompletePrediction) => {
      if (!placesService) return;

      return new Promise<FilterLocationOption>((resolve) => {
        const getDetailRequest: google.maps.places.PlaceDetailsRequest = {
          placeId: value.place_id,
        };
        const getDetailCallback: (
          a: google.maps.places.PlaceResult | null,
          b: google.maps.places.PlacesServiceStatus,
        ) => void = (res) => {
          const placeInfo = parseGooglePlaceInfoForCreateAddress(
            res?.address_components,
          );
          const lat = res?.geometry?.location?.lat() ?? 0;
          const lng = res?.geometry?.location?.lng() ?? 0;

          const operatingHours = mapGooglePeriodsToOperatingHours(
            res?.opening_hours?.periods,
          );

          resolve({
            name: res?.formatted_address || value.description,
            id: value.place_id,
            location: {
              lat,
              lng,
              infoAddress: {
                state: placeInfo.state,
                country: placeInfo.country,
                countryCode: placeInfo.countryCode,
                zip: placeInfo.zip,
                city: placeInfo.city,
              },
              operatingHours,
            },
          });
        };

        placesService.getDetails(getDetailRequest, getDetailCallback);
      });
    },
    [placesService],
  );

  const googlePlaceDetailOrUndefined = useCallback(
    async (googlePlace: PlaceType) => {
      try {
        return await handleGetGooglePlaceDetail(googlePlace);
      } catch (error) {
        return undefined;
      }
    },
    [handleGetGooglePlaceDetail],
  );

  return { googlePlaceDetailOrUndefined };
}
